import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import aksharArtLogoFull from "../../assets/logos/akshar-art-logo-full.svg";
import aksharArtLogoMark from "../../assets/logos/akshar-art-logo-mark.svg";
import { TEACHER_NAV_ITEMS, STUDENT_NAV_ITEMS, ROUTES } from "../../constants";
import { NavItem } from "../../types";
import { logout, useAppDispatch, useAppSelector } from "../../redux";
import { showInfo } from "../../services";
import dashboardIcon from "../../assets/icons/sidebar/dashboard.svg";
import studentsIcon from "../../assets/icons/sidebar/students.svg";
import attendanceIcon from "../../assets/icons/sidebar/attendance.svg";
import contentIcon from "../../assets/icons/sidebar/content.svg";
import chevronLeftIcon from "../../assets/icons/chevron-left.svg";
import chevronRightIcon from "../../assets/icons/chevron-right.svg";
import logoutIcon from "../../assets/icons/sidebar/logout.svg";
import "./Sidebar.css";

const ICON_MAP: Record<string, string> = {
  dashboard: dashboardIcon,
  students: studentsIcon,
  attendance: attendanceIcon,
  content: contentIcon,
};

export interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  onToggle,
  isMobileOpen,
  onMobileClose,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const role = useAppSelector((state) => state.auth.role);

  const navItems: NavItem[] =
    role === "student" ? STUDENT_NAV_ITEMS : TEACHER_NAV_ITEMS;

  const handleLogout = () => {
    if (onMobileClose) {
      onMobileClose();
    }
    dispatch(logout());
    showInfo("You have been logged out.");
    navigate(ROUTES.LOGIN, { replace: true });
  };

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      <div
        className={`sidebar-backdrop ${isMobileOpen ? "visible" : ""}`}
        onClick={onMobileClose}
        aria-hidden="true"
      />

      <aside
        className={`sidebar ${isCollapsed ? "collapsed" : "expanded"} ${
          isMobileOpen ? "mobile-open" : ""
        }`}
        aria-label="Sidebar Navigation"
      >
        {/* Sidebar Header */}
        <div className="sidebar-header">
          <div className="sidebar-logo-container">
            <img
              src={aksharArtLogoFull}
              alt="Akshar Art Logo"
              className={`sidebar-logo-full ${isCollapsed ? "hidden" : "visible"}`}
            />
            <img
              src={aksharArtLogoMark}
              alt="Akshar Art Mark"
              className={`sidebar-logo-mark ${isCollapsed ? "visible" : "hidden"}`}
            />
          </div>

          <button
            type="button"
            className="sidebar-toggle-btn"
            onClick={onToggle}
            aria-label={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            <img
              src={isCollapsed ? chevronRightIcon : chevronLeftIcon}
              alt={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
              width={18}
              height={18}
            />
            <span className="sidebar-tooltip toggle-tooltip" role="tooltip">
              {isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            </span>
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="sidebar-nav">
          <ul className="sidebar-menu">
            {navItems.map((item) => {
              const iconSrc = ICON_MAP[item.iconKey];
              return (
                <li key={item.path} className="sidebar-menu-item">
                  <NavLink
                    to={item.path}
                    end
                    className={({ isActive }) =>
                      `sidebar-link ${isActive ? "active" : ""}`
                    }
                    onClick={() => {
                      if (onMobileClose) {
                        onMobileClose();
                      }
                    }}
                  >
                    <span className="sidebar-icon">
                      {iconSrc && (
                        <img
                          src={iconSrc}
                          alt={item.label}
                          width={20}
                          height={20}
                        />
                      )}
                    </span>
                    <span className="sidebar-label">{item.label}</span>
                    <span className="sidebar-tooltip" role="tooltip">
                      {item.label}
                    </span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer / Logout */}
        <div className="sidebar-footer">
          <button
            type="button"
            className="sidebar-logout-btn"
            onClick={handleLogout}
            aria-label="Logout"
          >
            <span className="sidebar-icon">
              <img src={logoutIcon} alt="Logout" width={20} height={20} />
            </span>
            <span className="sidebar-label">Logout</span>
            <span className="sidebar-tooltip" role="tooltip">
              Logout
            </span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
